import { useState, useEffect } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { AdminPrequalQuestion, AdminPrequalConfig } from '@vedjegy/shared';

export default function AdminPrequalQuestionsPage() {
  const [questions, setQuestions] = useState<AdminPrequalQuestion[]>([]);
  const [config, setConfig]       = useState<AdminPrequalConfig | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  useEffect(() => {
    api.adminPrequalification.listQuestions()
      .then((data) => {
        setQuestions(data.questions);
        setConfig(data.config);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Szerverhiba.'))
      .finally(() => setLoading(false));
  }, []);

  const maxPossibleScore = questions
    .filter((q) => q.isActive)
    .reduce((sum, q) => sum + Math.max(0, ...q.options.map((o) => o.score)), 0);

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
            ADMIN
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>
            Előminősítési kérdések
          </h1>
        </div>

        {loading && (
          <div style={{ color: 'var(--muted-foreground)' }}>Betöltés…</div>
        )}
        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 24 }}>
            <span>⚠</span><div><p>{error}</p></div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Config card */}
            {config && (
              <div style={{
                background: '#fff',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: '20px 28px',
                marginBottom: 28,
                display: 'flex',
                gap: 40,
                alignItems: 'center',
                boxShadow: 'var(--shadow-sm)',
              }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 4 }}>
                    Megfelelési küszöb
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
                    {Math.round(config.passThreshold * 100)}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 4 }}>
                    Min. kiértékelhető pont
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
                    {config.minScoreToEvaluate}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 4 }}>
                    Max. elérhető pont (aktív)
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
                    {maxPossibleScore}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 4 }}>
                    Kérdések száma
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>
                    {questions.filter((q) => q.isActive).length}
                    <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--muted-foreground)', marginLeft: 4 }}>
                      / {questions.length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Questions list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {questions.map((q) => {
                const maxScore = Math.max(0, ...q.options.map((o) => o.score));
                return (
                  <div key={q.id} style={{
                    background: '#fff',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '22px 28px',
                    boxShadow: 'var(--shadow-sm)',
                    opacity: q.isActive ? 1 : 0.55,
                  }}>
                    {/* Question header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          background: 'var(--primary)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700,
                        }}>
                          {q.questionNumber}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)', lineHeight: 1.4, paddingTop: 4 }}>
                          {q.text}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        {q.skipWhenNoEmployees && (
                          <span className="badge" style={{ background: 'rgba(0,48,46,.08)', color: 'var(--primary)', fontSize: 11 }}>
                            csak munkavállalóval
                          </span>
                        )}
                        {!q.isActive && (
                          <span className="badge" style={{ background: 'rgba(209,49,46,.1)', color: 'var(--destructive)', fontSize: 11 }}>
                            inaktív
                          </span>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', background: 'var(--muted)', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>
                          max {maxScore} pont
                        </span>
                      </div>
                    </div>

                    {/* Options */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {q.options.map((opt) => {
                        const isMax = opt.score === maxScore && maxScore > 0;
                        return (
                          <div key={opt.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px',
                            background: isMax ? 'rgba(78,192,124,.08)' : 'var(--muted)',
                            borderRadius: 10,
                            border: isMax ? '1px solid rgba(78,192,124,.3)' : '1px solid transparent',
                            gap: 12,
                          }}>
                            <span style={{ fontSize: 14, color: 'var(--primary)', lineHeight: 1.4 }}>
                              {opt.text}
                            </span>
                            <span style={{
                              fontSize: 13, fontWeight: 700, flexShrink: 0,
                              color: opt.score > 0 ? '#064935' : opt.score < 0 ? 'var(--destructive)' : 'var(--muted-foreground)',
                              background: opt.score > 0 ? 'rgba(78,192,124,.15)' : opt.score < 0 ? 'rgba(209,49,46,.1)' : 'transparent',
                              borderRadius: 6, padding: '2px 8px',
                              minWidth: 42, textAlign: 'center',
                            }}>
                              {opt.score > 0 ? '+' : ''}{opt.score}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
